import { useState, useEffect } from 'react';
import { X, AlertTriangle, CheckCircle } from 'lucide-react';
import { getRestorationPlan, restoreItem } from '../api/recycleBin';

const RestoreModal = ({ item, isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [fetchingPlan, setFetchingPlan] = useState(false);
  const [error, setError] = useState('');
  const [restorationPlan, setRestorationPlan] = useState(null);
  const [options, setOptions] = useState({
    include_dependencies: true,
    cascade_parents: true,
    cascade_children: false,
    restoration_reason: ''
  });

  useEffect(() => {
    if (isOpen && item) {
      fetchRestorationPlan();
    }
  }, [isOpen, item]);

  const fetchRestorationPlan = async () => {
    try {
      setFetchingPlan(true);
      setError('');
      const response = await getRestorationPlan(item.id);
      setRestorationPlan(response.data);
    } catch (err) {
      setError(err.message || 'Failed to fetch restoration plan');
    } finally {
      setFetchingPlan(false);
    }
  };

  const handleRestore = async () => {
    if (!options.restoration_reason.trim()) {
      setError('Please provide a reason for restoration');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await restoreItem(item.id, options);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to restore item');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Restore Item</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Item Details */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">Item Details</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-500">Type:</span>
                  <span className="ml-2 font-medium text-gray-900 capitalize">
                    {item?.entity_type?.replace(/_/g, ' ')}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Deleted:</span>
                  <span className="ml-2 font-medium text-gray-900">{formatDate(item?.deleted_at)}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-500">Deleted By:</span>
                  <span className="ml-2 font-medium text-gray-900">{item?.deleted_by_name || 'Unknown'}</span>
                </div>
                {item?.deletion_reason && (
                  <div className="col-span-2">
                    <span className="text-gray-500">Reason:</span>
                    <span className="ml-2 text-gray-900">{item.deletion_reason}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Restoration Plan */}
            {fetchingPlan ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : restorationPlan && (
              <>
                {/* Conflicts Warning */}
                {restorationPlan.conflicts && restorationPlan.conflicts.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <h4 className="font-medium text-red-900 mb-2">Restoration Conflicts Detected</h4>
                        <ul className="space-y-1 text-sm text-red-800">
                          {restorationPlan.conflicts.map((conflict, index) => (
                            <li key={index}>{conflict.message}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Dependencies */}
                {restorationPlan.dependencies && (
                  <div className="space-y-4">
                    {/* Parent Dependencies */}
                    {restorationPlan.dependencies.parents && restorationPlan.dependencies.parents.length > 0 && (
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                          <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
                          Required Parent Items ({restorationPlan.dependencies.parents.length})
                        </h4>
                        <div className="space-y-2 text-sm">
                          {restorationPlan.dependencies.parents.map((dep, index) => (
                            <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                              <div>
                                <span className="font-medium capitalize">{dep.entityType.replace(/_/g, ' ')}</span>
                                <span className="text-gray-500 ml-2">ID: {dep.entityId}</span>
                              </div>
                              <span className={`text-xs px-2 py-1 rounded ${
                                dep.isDeleted ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                              }`}>
                                {dep.isDeleted ? 'Deleted' : 'Active'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Child Dependencies */}
                    {restorationPlan.dependencies.children && restorationPlan.dependencies.children.length > 0 && (
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-3">
                          Child Items ({restorationPlan.dependencies.children.length})
                        </h4>
                        <div className="space-y-2 text-sm">
                          {restorationPlan.dependencies.children.slice(0, 5).map((dep, index) => (
                            <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                              <div>
                                <span className="font-medium capitalize">{dep.entityType.replace(/_/g, ' ')}</span>
                                <span className="text-gray-500 ml-2">ID: {dep.entityId}</span>
                              </div>
                              <span className={`text-xs px-2 py-1 rounded ${
                                dep.isDeleted ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                              }`}>
                                {dep.isDeleted ? 'Deleted' : 'Active'}
                              </span>
                            </div>
                          ))}
                          {restorationPlan.dependencies.children.length > 5 && (
                            <p className="text-gray-500 text-xs mt-2">
                              ... and {restorationPlan.dependencies.children.length - 5} more
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Restoration Options */}
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900">Restoration Options</h4>

              <div className="space-y-3">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={options.include_dependencies}
                    onChange={(e) => setOptions({ ...options, include_dependencies: e.target.checked })}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-3">
                    <span className="block text-sm font-medium text-gray-900">Include Dependencies</span>
                    <span className="block text-xs text-gray-500">Restore related items if needed</span>
                  </span>
                </label>

                {options.include_dependencies && (
                  <>
                    <label className="flex items-start ml-7">
                      <input
                        type="checkbox"
                        checked={options.cascade_parents}
                        onChange={(e) => setOptions({ ...options, cascade_parents: e.target.checked })}
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-3">
                        <span className="block text-sm font-medium text-gray-900">Cascade Parents</span>
                        <span className="block text-xs text-gray-500">Automatically restore required parent items</span>
                      </span>
                    </label>

                    <label className="flex items-start ml-7">
                      <input
                        type="checkbox"
                        checked={options.cascade_children}
                        onChange={(e) => setOptions({ ...options, cascade_children: e.target.checked })}
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-3">
                        <span className="block text-sm font-medium text-gray-900">Cascade Children</span>
                        <span className="block text-xs text-gray-500">Also restore child items</span>
                      </span>
                    </label>
                  </>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Restoration Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={options.restoration_reason}
                  onChange={(e) => setOptions({ ...options, restoration_reason: e.target.value })}
                  rows={3}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter reason for restoration..."
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleRestore}
              disabled={loading || fetchingPlan || (restorationPlan?.conflicts && restorationPlan.conflicts.length > 0)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Restoring...
                </>
              ) : (
                <>
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Restore Item
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestoreModal;
